/**
 * LogoAnimated - Animated knockout logo with transparent eyes and stroke
 *
 * Uses SVG mask to create true transparent holes:
 * - Eyes: knockout (show background through), animated via --eyes-rot
 * - Stroke: knockout (transparent outline around body)
 * - Body: filled with currentColor
 *
 * CSS Custom Properties:
 * - --rot: Main rotation of both halves (default: 0deg)
 * - --eyes-rot: Independent eye rotation (default: 0deg)
 * - --opening: Gap angle between halves (default: 50deg)
 * - --left-eye / --right-eye: That eye's place along the eyes' axis, 1 = its own side, -1 = the other eye's (default: 1)
 * - --left-eye-size / --right-eye-size: That eye's scale, 0 = gone (default: 1)
 *
 * Geometry:
 * - ViewBox: 0 0 128 128, center at (64, 64)
 * - Semicircle radius: 60
 * - Eyes at (-33, 0) and (33, 0) relative to center, radius 9
 * - Stroke width: 3px (knockout)
 */
import { useId } from 'react'

const LogoAnimated = ({ ref, height = 64, bands = false, ...props }) => {
	const id = useId()

	return (
		<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128' ref={ref} height={height} {...props}>
			<defs>
				<style>
					{`
						@property --rot {
							syntax: '<angle>';
							inherits: true;
							initial-value: 0deg;
						}

						@property --eyes-rot {
							syntax: '<angle>';
							inherits: true;
							initial-value: 0deg;
						}

						@property --opening {
							syntax: '<angle>';
							inherits: true;
							initial-value: 0deg;
						}

						@property --eye-r {
							syntax: '<number>';
							inherits: true;
							initial-value: 9;
						}

						svg {
							--eyes-rot: 0deg;
							--opening: 50deg;
							--rot: 0deg;
							--eye-r: 9;
						}

						#logo_l_${id} {
							transform: rotate(calc(var(--rot) + 180deg - var(--opening) / 2));
						}

						#logo_r_${id} {
							transform: rotate(calc(var(--rot) + var(--opening) / 2));
						}

						#knockout_eyes_${id} {
							transform: rotate(var(--eyes-rot));
							transform-origin: 0 0;
						}

						.logo_half_mask_${id} {
							fill: white;
							stroke: black;
							stroke-width: 4px;
						}
					`}
				</style>

				{/* Semicircle definitions */}
				<g id={`logo_l_${id}`}>
					<path d='M60 0 A60 60 0 0 1 60 120 Z' transform='translate(-60, -60)' />
				</g>
				<g id={`logo_r_${id}`}>
					<path d='M60 0 A60 60 0 0 1 60 120 Z' transform='translate(-60, -60)' />
				</g>

				{/* Knockout mask - black=transparent, white=visible */}
				<mask id={`knockout_mask_${id}`}>
					{/* Start with everything transparent */}
					<rect fill='black' x='-70' y='-70' width='140' height='140' />
					{/* Logo body - white fill (visible), black stroke (knockout edge) */}
					<use href={`#logo_l_${id}`} className={`logo_half_mask_${id}`} />
					<use href={`#logo_r_${id}`} className={`logo_half_mask_${id}`} />
					{/* Eye knockouts — scale instead of CSS r because iOS Safari can't resolve var() for SVG geometry properties */}
					<g id={`knockout_eyes_${id}`}>
						<circle
							fill='black'
							cx='-33'
							cy='0'
							r='9'
							style={{
								transform:
									'translateX(calc((1 - var(--left-eye, 1)) * 33px)) scale(calc(var(--eye-r) / 9 * var(--left-eye-size, 1)))',
								transformOrigin: '-33px 0',
							}}
						/>
						<circle
							fill='black'
							cx='33'
							cy='0'
							r='9'
							style={{
								transform:
									'translateX(calc((var(--right-eye, 1) - 1) * 33px)) scale(calc(var(--eye-r) / 9 * var(--right-eye-size, 1)))',
								transformOrigin: '33px 0',
							}}
						/>
					</g>
				</mask>
			</defs>

			{/* Logo body with knockout mask applied */}
			<g transform='translate(64 64)'>
				<rect fill='currentColor' mask={`url(#knockout_mask_${id})`} x='-70' y='-70' width='140' height='140' />
				{bands && (
					<g transform='rotate(-90)'>
						<path
							className='logo_band_1'
							d='M16.91,-6.16 A18,18 0 0,1 16.91,6.16 L3.76,1.37 A4,4 0 0,0 3.76,-1.37 Z'
							fill='currentColor'
						/>
						<path
							className='logo_band_2'
							d='M33.83,-12.31 A36,36 0 0,1 33.83,12.31 L20.67,7.52 A22,22 0 0,0 20.67,-7.52 Z'
							fill='currentColor'
						/>
						<path
							className='logo_band_3'
							d='M50.74,-18.47 A54,54 0 0,1 50.74,18.47 L37.59,13.68 A40,40 0 0,0 37.59,-13.68 Z'
							fill='currentColor'
						/>
					</g>
				)}
			</g>
		</svg>
	)
}

LogoAnimated.displayName = 'LogoAnimated'

export default LogoAnimated
